import { useMemo } from 'react';
import { SectionCard, fmtTRY } from '../yeni-design/primitives';
import { oranMetni } from './bicim';
import { BlokIskeleti } from './BlokIskeleti';
import type { Donem } from './donem';
import type { SaKaynak } from './ekranKaynaklari';
import { kategoriTablosu } from './KategoriBlok';
import type { SaKategoriSatiri } from './ozetHesaplari';
import { useSaSorgu } from './useSatisVerisi';

interface Props {
  kaynak: SaKaynak | undefined;
  donem: Donem;
  oncekiDonem: Donem;
  sube?: string | null;
  kanal?: string | null;
  onKategoriSec?: (kategori: string) => void;
}

/** Karlilik : kategoriler marja gore (990051, sayfa anahtari; ek istek yok). Cubuk en yuksek marja oranlanir. */
export function KategoriMarjBlok({ kaynak, donem, oncekiDonem, sube = null, kanal = null, onKategoriSec }: Props) {
  const q = useSaSorgu<SaKategoriSatiri>(kaynak, { donem, sube, kanal, onceki: oncekiDonem });
  const satirlar = useMemo(
    () =>
      kategoriTablosu(q.data?.data ?? [], Number.POSITIVE_INFINITY)
        .filter((k) => k.marj !== null)
        .sort((a, b) => (b.marj ?? 0) - (a.marj ?? 0)),
    [q.data],
  );

  if (!kaynak) return null;
  if (q.isLoading) return <BlokIskeleti testid="blok-kategori-marj-iskelet" baslik="Kategori marjları" yukseklik={320} satir={8} />;
  if (satirlar.length === 0) return null;
  const enYuksek = Math.max(...satirlar.map((k) => k.marj ?? 0), 1);

  return (
    <div data-testid="blok-kategori-marj">
      <SectionCard title="Kategori marjları" subtitle="Yüksekten düşüğe">
        <ul className="m-0 p-0 list-none flex flex-col gap-2.5">
          {satirlar.map((k) => {
            const marj = k.marj ?? 0;
            return (
              <li key={k.ad} data-testid={`kategori-marj-${k.ad}`} className="flex flex-col gap-1">
                <div className="flex items-baseline justify-between gap-3 text-[12.5px]">
                  {onKategoriSec ? (
                    <button type="button" onClick={() => onKategoriSec(k.ad)} className="focus-ring min-w-0 truncate text-left text-ink-800 hover:underline underline-offset-2">
                      {k.ad}
                    </button>
                  ) : (
                    <span className="min-w-0 truncate text-ink-800">{k.ad}</span>
                  )}
                  <span className="shrink-0 tnum text-ink-500">
                    {fmtTRY(k.ciro)} · <span className={marj < 0 ? 'text-neg-700' : 'text-ink-900'}>{oranMetni(marj, 1)}</span>
                  </span>
                </div>
                <div className="h-1.5 rounded-full bg-border-light overflow-hidden">
                  <div
                    className={`h-full rounded-full ${marj < 0 ? 'bg-neg-700' : 'bg-ink-700'}`}
                    style={{ width: `${Math.min(100, (Math.abs(marj) / enYuksek) * 100)}%` }}
                  />
                </div>
              </li>
            );
          })}
        </ul>
      </SectionCard>
    </div>
  );
}
